import type { Path, Point, TElement, TRange } from '@platejs/slate';
import * as Sentry from '@sentry/react';
import { NodeApi, RangeApi } from 'platejs';
import type { PlateEditor } from 'platejs/react';
import { createPlatePlugin } from 'platejs/react';
import { flushSync } from 'react-dom';

/**
 * Robust handling of the `beforeinput` events browser writing extensions
 * (Grammarly, LanguageTool, platform spellcheck) dispatch when the user
 * accepts a suggestion. Grammarly recognises Slate editors by the
 * `data-slate-editor` attribute and applies a card fix ("Correct the
 * sentence") as a SEQUENCE of steps; per step it sets the DOM selection over
 * the target text and dispatches one synthetic (`isTrusted: false`) event:
 *
 * - `insertReplacementText` (with a `DataTransfer`) for word replacements,
 * - `deleteContentBackward` / `deleteContentForward` for deletions,
 * - `insertText` for insertions.
 *
 * ## Why slate's default handling corrupts content
 *
 * slate-react resolves event target ranges through DOM↔model WeakMaps that
 * go stale the moment a prior edit lands in the same frame (React has not
 * re-rendered yet). Three corruption modes, all reproduced against customer
 * reports (2026-09):
 *
 * 1. `insertReplacementText` carries a `DataTransfer`, so slate routes it
 *    through the full paste pipeline. With stale mappings the range silently
 *    falls back to the internal selection — the replacement is inserted at
 *    the wrong place WITHOUT deleting the original ("…it were sent.was"),
 *    and multi-line payloads are deserialized into blocks that split the
 *    paragraph mid-sentence.
 * 2. Delete events NEVER consult `targetRanges` in slate-react — they rely
 *    entirely on the "extension sets the DOM selection immediately before
 *    dispatching" handshake. Mid-burst, that selection resolves against
 *    stale mappings (or not at all) and the delete falls through to the
 *    editor's current selection, chewing characters near the previous fix
 *    ("That's right" → "That's rig").
 * 3. `insertText` with an unmappable target range likewise inserts at the
 *    stale internal selection ("That's" → "a That's").
 *
 * ## What this plugin does instead
 *
 * These events describe edits to flowing text, so they are applied as exactly
 * that:
 *
 * 1. Resolve the event's target — its first `StaticRange`, or for
 *    selection-handshake events (deletes, `insertText`) the LIVE DOM
 *    selection the extension just set — via the DOM mapping, then VALIDATE
 *    it: the model text at the resolved range must equal the DOM text the
 *    range spans.
 * 2. On mismatch or failure, fall back to text-anchored resolution: locate
 *    the affected string inside its block by content, choosing the
 *    occurrence nearest the range's DOM offset (robust against same-frame
 *    staleness).
 * 3. Apply the edit as inline `insertText` / range deletion at the resolved
 *    range — never through HTML deserialization — with newlines collapsed to
 *    spaces, so a suggestion can never split the paragraph into blocks. The
 *    application is wrapped in `flushSync` so the DOM is committed before
 *    the extension computes its NEXT step's selection from it.
 * 4. If the affected text cannot be located in the current model at all,
 *    DROP the event: a suggestion that fails to apply beats corrupted
 *    content.
 *
 * `insertText` and the delete input types are also what REAL typing
 * dispatches, so for those the robust path is gated on `isTrusted: false`
 * (extension-dispatched) — trusted keyboard input takes slate's default
 * pipeline untouched. `insertReplacementText` is handled for any trust level
 * (macOS autocorrect dispatches it trusted, with the same range semantics).
 * Payloads that are not plain-text replacements (files, in-app slate
 * fragments) are left to the default pipeline untouched.
 */

/** In-app clipboard type slate stamps on copies; never a text replacement. */
const SLATE_FRAGMENT_MIME = 'application/x-slate-fragment';

/**
 * The subset of `StaticRange` the handler reads. Structural so tests (and
 * jsdom, which lacks a `StaticRange` constructor) can supply plain objects.
 */
export interface StaticRangeLike {
  startContainer: Node;
  startOffset: number;
  endContainer: Node;
  endOffset: number;
}

/** The subset of `DataTransfer` the handler reads. */
interface ReplacementDataTransfer {
  readonly files: { readonly length: number };
  readonly types: readonly string[];
  getData: (format: string) => string;
}

/**
 * The subset of the native `InputEvent` the handler reads. `InputEvent`
 * satisfies this structurally.
 */
export interface ReplacementBeforeInputEvent {
  readonly inputType: string;
  /**
   * `false` for extension-dispatched synthetic events (all of Grammarly's),
   * `true` for real user input. Gates the robust handling of the input types
   * that genuine typing also produces (`insertText`, deletes).
   */
  readonly isTrusted: boolean;
  /** `insertText` payload (`InputEvent.data`). */
  readonly data: string | null;
  readonly dataTransfer: ReplacementDataTransfer | null;
  getTargetRanges: () => readonly StaticRangeLike[];
  preventDefault: () => void;
}

/**
 * Newlines collapsed to single spaces: a replacement is flowing text within a
 * sentence — block/line structure must come from the user, never from a
 * suggestion payload (that is the "inserting large blocks" bug).
 */
export function normalizeReplacementText(text: string): string {
  return text.replace(/[^\S\r\n]*(?:\r\n|\r|\n)+[^\S\r\n]*/g, ' ');
}

/**
 * Index of the occurrence of `needle` in `haystack` closest to
 * `preferredIndex`, or null when absent. Distance is measured from the
 * occurrence's start.
 */
export function nearestOccurrence(
  haystack: string,
  needle: string,
  preferredIndex: number
): number | null {
  if (!needle) {
    return null;
  }
  let best: number | null = null;
  let from = 0;
  for (;;) {
    const i = haystack.indexOf(needle, from);
    if (i === -1) {
      break;
    }
    if (
      best === null ||
      Math.abs(i - preferredIndex) < Math.abs(best - preferredIndex)
    ) {
      best = i;
    }
    from = i + 1;
  }
  return best;
}

/**
 * The text the static range spans, read from the live DOM. Null when the
 * range's nodes are detached or its offsets no longer fit (extensions hold
 * ranges across our re-renders).
 */
function staticRangeText(sr: StaticRangeLike): string | null {
  if (!sr.startContainer.isConnected || !sr.endContainer.isConnected) {
    return null;
  }
  try {
    const range = document.createRange();
    range.setStart(sr.startContainer, sr.startOffset);
    range.setEnd(sr.endContainer, sr.endOffset);
    return range.toString();
  } catch {
    return null;
  }
}

/**
 * Character offset of an element-boundary DOM position — `container` is an
 * element and `offset` is a child index — within `blockEl`'s visible text: the
 * summed visible length of every text node that ends at or before the
 * (container, offset) point. Zero-width placeholder spans are excluded, so the
 * result lines up with the slate model string. Null when the point cannot be
 * ranged (detached nodes / bad offset). Extensions collapse the DOM selection
 * to an element boundary (e.g. before an inline mark) rather than into a text
 * node; without this the text-anchored fallback loses the offset and targets
 * the first repeated occurrence.
 */
function domOffsetAtElementBoundary(
  blockEl: Element,
  container: Element,
  offset: number
): number | null {
  let point: Range;
  try {
    point = document.createRange();
    point.setStart(blockEl, 0);
    point.setEnd(container, offset);
  } catch {
    return null;
  }
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const textNode = node as Text;
    // comparePoint > 0 ⇒ the end of this text node lies AFTER the boundary
    // point, so the point precedes it and all visible text before the point
    // is already counted.
    if (point.comparePoint(textNode, textNode.data.length) > 0) {
      return total;
    }
    const isZeroWidth =
      (textNode.parentElement?.closest('[data-slate-zero-width]') ?? null) !==
      null;
    if (!isZeroWidth) {
      total += textNode.data.length;
    }
  }
  return total;
}

/**
 * Character offset of (container, offset) within `blockEl`'s visible text,
 * mirroring the slate model's string for the block: text inside
 * `[data-slate-zero-width]` spans (slate's placeholder U+FEFF characters) is
 * excluded. Handles both a text-node container (offset is a character index)
 * and an element-boundary container (offset is a child index — the extension's
 * live DOM selection can start there). Null when `container` is neither, or is
 * not inside the block.
 */
function domOffsetInBlock(
  blockEl: Element,
  container: Node,
  offset: number
): number | null {
  if (container.nodeType === Node.ELEMENT_NODE) {
    return domOffsetAtElementBoundary(blockEl, container as Element, offset);
  }
  if (container.nodeType !== Node.TEXT_NODE) {
    return null;
  }
  const walker = document.createTreeWalker(blockEl, NodeFilter.SHOW_TEXT);
  let total = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const isZeroWidth =
      (node.parentElement?.closest('[data-slate-zero-width]') ?? null) !== null;
    if (node === container) {
      return total + (isZeroWidth ? 0 : offset);
    }
    if (!isZeroWidth) {
      total += (node as Text).data.length;
    }
  }
  return null;
}

/**
 * The point at character offset `target` within the block's concatenated leaf
 * text, or null when the offset runs past the block's end.
 */
function pointAtBlockOffset(
  block: TElement,
  blockPath: Path,
  target: number
): Point | null {
  let remaining = target;
  for (const [textNode, relPath] of NodeApi.texts(block)) {
    const len = textNode.text.length;
    if (remaining <= len) {
      return { offset: remaining, path: [...blockPath, ...relPath] };
    }
    remaining -= len;
  }
  return null;
}

/**
 * Resolve where the replacement applies in the CURRENT model.
 *
 * Order of preference: validated direct DOM→model mapping; text-anchored
 * search within the containing block; null (caller drops the event).
 */
function resolveReplacementRange(
  editor: PlateEditor,
  staticRange: StaticRangeLike | null
): TRange | null {
  if (!staticRange) {
    // No target range on the event: the only trustworthy anchor is an
    // expanded editor selection (what slate itself would use). A collapsed
    // selection cannot describe a replacement target — drop.
    const sel = editor.selection;
    return sel && RangeApi.isExpanded(sel) ? sel : null;
  }

  const oldText = staticRangeText(staticRange);
  // Plate's toSlateRange wrapper always suppresses throws (returns
  // null/undefined for unmappable ranges). Cast: the runtime accepts any
  // StaticRange-shaped object; the declared type only admits the DOM classes.
  const direct = editor.api.toSlateRange(
    staticRange as unknown as globalThis.StaticRange,
    { exactMatch: false, suppressThrow: true }
  );
  if (direct != null && oldText != null) {
    if (oldText === '' && RangeApi.isCollapsed(direct)) {
      // Zero-length target: a pure insertion at a point — nothing to validate
      // against, accept the direct mapping.
      return direct;
    }
    if (oldText !== '' && editor.api.string(direct) === oldText) {
      return direct;
    }
  }
  if (!oldText) {
    return null;
  }

  // Text-anchored fallback: the mapping is stale (a prior replacement in the
  // same frame) or wrong. Find the block element, then locate the replaced
  // string in the CURRENT model text of that block.
  const startEl =
    staticRange.startContainer instanceof Element
      ? staticRange.startContainer
      : staticRange.startContainer.parentElement;
  const blockEl = startEl?.closest('[data-slate-node="element"]');
  if (!blockEl) {
    return null;
  }
  const domNode = editor.api.toSlateNode(blockEl);
  const blockPath = domNode ? editor.api.findPath(domNode) : undefined;
  if (!blockPath) {
    return null;
  }
  const entry = editor.api.node<TElement>(blockPath);
  if (!entry) {
    return null;
  }
  const [block] = entry;
  const blockText = NodeApi.string(block);
  const domOffset = domOffsetInBlock(
    blockEl,
    staticRange.startContainer,
    staticRange.startOffset
  );
  const idx =
    domOffset !== null && blockText.startsWith(oldText, domOffset)
      ? domOffset
      : nearestOccurrence(blockText, oldText, domOffset ?? 0);
  if (idx === null) {
    return null;
  }
  const anchor = pointAtBlockOffset(block, blockPath, idx);
  const focus = pointAtBlockOffset(block, blockPath, idx + oldText.length);
  return anchor && focus ? { anchor, focus } : null;
}

/**
 * The live DOM selection as a StaticRangeLike, or null when there is none.
 * Selection-handshake events (deletes, `insertText`) carry their target here:
 * the extension sets the DOM selection immediately before dispatching. Read
 * directly from the DOM — slate's own view of the selection is DEBOUNCED
 * (`scheduleOnDOMSelectionChange`) and has not seen this selection yet when
 * the beforeinput handler runs.
 */
function domSelectionStaticRange(): StaticRangeLike | null {
  const sel = document.getSelection();
  if (!sel || sel.rangeCount === 0) {
    return null;
  }
  const r = sel.getRangeAt(0);
  return {
    endContainer: r.endContainer,
    endOffset: r.endOffset,
    startContainer: r.startContainer,
    startOffset: r.startOffset,
  };
}

/**
 * Apply an editor mutation and synchronously commit the resulting re-render
 * to the DOM. Extensions verify each applied step against the DOM (and
 * compute the NEXT step's selection from it) before React's batched render
 * would normally flush — without this, every later step in a multi-fix burst
 * targets a stale document. Safe here: the plugin runs from a native
 * beforeinput listener, never inside React's own render/commit.
 */
function applyFlushed(fn: () => void): void {
  flushSync(fn);
}

/**
 * Replace `at` with `text` (inline, single block). Select-then-insert mirrors
 * slate's own handling: an expanded selection is deleted and the caret ends
 * after the inserted text.
 */
function applyTextEdit(editor: PlateEditor, at: TRange, text: string): void {
  applyFlushed(() => {
    editor.tf.select(at);
    const replacement = normalizeReplacementText(text);
    if (replacement) {
      editor.tf.insertText(replacement);
    } else if (editor.selection && RangeApi.isExpanded(editor.selection)) {
      // Empty payload over an expanded target = pure deletion (the extension
      // removes the text); insertText('') would be a no-op.
      editor.tf.deleteFragment();
    }
  });
}

/**
 * Fail-closed telemetry for a DROPPED extension edit — the target could not be
 * resolved in the current model, so the suggestion was consumed without
 * touching content. Recorded to the console for local debugging AND to Sentry:
 * console output is not guaranteed to be captured as a breadcrumb, so the
 * explicit `addBreadcrumb` is what makes these paths diagnosable in the field.
 */
function breadcrumbDropped(inputType: string, message: string): void {
  console.warn(message);
  Sentry.addBreadcrumb({
    category: 'grammarly.plugin',
    level: 'warning',
    message,
    data: { inputType, outcome: 'dropped' },
  });
}

/**
 * Fail-closed telemetry for an extension edit that THREW on the read/apply
 * path and was consumed rather than allowed to reach slate's corrupting
 * default. Console + Sentry, same rationale as {@link breadcrumbDropped}.
 */
function breadcrumbFailed(
  inputType: string,
  message: string,
  error: unknown
): void {
  console.error(message, error);
  Sentry.addBreadcrumb({
    category: 'grammarly.plugin',
    level: 'error',
    message,
    data: {
      inputType,
      outcome: 'error',
      error: error instanceof Error ? error.message : String(error),
    },
  });
}

/**
 * Handle one `insertReplacementText` beforeinput event. Returns `true` when
 * the event was consumed (slate's default handling must not run), `undefined`
 * when it is not a plain-text replacement and the default pipeline should
 * proceed.
 */
export function handleInsertReplacementText(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent
): true | undefined {
  if (event.inputType !== 'insertReplacementText') {
    return undefined;
  }
  const dt = event.dataTransfer;
  if (!dt) {
    return undefined;
  }
  // Files or in-app slate fragments are not text replacements — leave them to
  // the default insertData pipeline.
  if (dt.files.length > 0 || dt.getData(SLATE_FRAGMENT_MIME)) {
    return undefined;
  }
  // `getData` returns '' both for an absent type and for an empty value, so
  // check `types`: an ABSENT text/plain payload is not a text replacement
  // (defer to the default pipeline), while a PRESENT-but-empty one is a valid
  // deletion suggestion (the extension removes the target text) and must be
  // handled here — slate's default path silently drops it.
  if (!dt.types.includes('text/plain')) {
    return undefined;
  }
  const text = dt.getData('text/plain');

  let targetRange: StaticRangeLike | undefined;
  try {
    [targetRange] = event.getTargetRanges();
    // Both calls in this try/catch can throw; the catch below fail-closes
    // regardless of which one did.
    event.preventDefault();
  } catch (e) {
    try {
      event.preventDefault();
    } catch {
      // preventDefault itself may be what threw; swallow the second failure —
      // returning true below is what ultimately fail-closes the event.
    }
    breadcrumbFailed(
      event.inputType,
      `insertReplacementTextPlugin: failed to read target range for ${event.inputType}`,
      e
    );
    return true;
  }

  try {
    const at = resolveReplacementRange(editor, targetRange ?? null);
    if (!at) {
      // The replaced text no longer exists where the extension believes it
      // does. Dropping the suggestion is the only non-corrupting option.
      breadcrumbDropped(
        event.inputType,
        'insertReplacementTextPlugin: dropped unresolvable insertReplacementText'
      );
      return true;
    }
    applyTextEdit(editor, at, text);
  } catch (e) {
    // preventDefault already ran; consuming a suggestion on an internal error
    // is safer than letting the default pipeline corrupt the document.
    breadcrumbFailed(
      event.inputType,
      'insertReplacementTextPlugin: failed to apply',
      e
    );
  }
  return true;
}

/** Resolves an extension event's target; `true` means already consumed (dropped/logged), else the `TRange` to apply. */
function resolveUntrustedEventTarget(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent,
  inputType: string
): TRange | true {
  let target: StaticRangeLike | null;
  try {
    // Prefer the LIVE DOM selection the extension just set over a pre-edit
    // targetRange a prior step may have invalidated.
    const [targetRange] = event.getTargetRanges();
    target = domSelectionStaticRange() ?? targetRange ?? null;
    event.preventDefault();
  } catch (e) {
    try {
      event.preventDefault();
    } catch {
      // preventDefault itself may be what threw; swallow the second failure —
      // returning true below is what ultimately fail-closes the event.
    }
    breadcrumbFailed(
      inputType,
      `insertReplacementTextPlugin: failed to read target for extension ${inputType}`,
      e
    );
    return true;
  }
  if (!target) {
    breadcrumbDropped(
      inputType,
      `insertReplacementTextPlugin: dropped extension ${inputType} with no resolvable target`
    );
    return true;
  }
  try {
    const at = resolveReplacementRange(editor, target);
    if (at) {
      return at;
    }
    breadcrumbDropped(
      inputType,
      `insertReplacementTextPlugin: dropped unresolvable extension ${inputType}`
    );
    return true;
  } catch (e) {
    breadcrumbFailed(
      inputType,
      `insertReplacementTextPlugin: failed to resolve ${inputType}`,
      e
    );
    return true;
  }
}

/**
 * Handle one extension-dispatched (`isTrusted: false`) `insertText`
 * beforeinput event — the insertion step of a multi-fix rewrite. slate's
 * default handling inserts at the internal selection whenever the event's
 * target range fails to map (stale WeakMaps mid-burst), scattering the
 * insertion into unrelated text. Resolve the target robustly instead; drop
 * the event when it cannot be located.
 */
export function handleUntrustedInsertText(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent
): true | undefined {
  if (event.inputType !== 'insertText' || event.isTrusted) {
    return undefined;
  }
  const text =
    event.data ??
    (event.dataTransfer?.types.includes('text/plain')
      ? event.dataTransfer.getData('text/plain')
      : null);
  if (typeof text !== 'string') {
    return undefined;
  }
  const at = resolveUntrustedEventTarget(editor, event, event.inputType);
  if (at === true) {
    return true;
  }
  try {
    applyTextEdit(editor, at, text);
  } catch (e) {
    breadcrumbFailed(
      event.inputType,
      `insertReplacementTextPlugin: failed to apply ${event.inputType}`,
      e
    );
  }
  return true;
}

/**
 * Handle one extension-dispatched (`isTrusted: false`) delete beforeinput
 * event — the deletion step of a multi-fix rewrite. slate-react NEVER
 * consults `targetRanges` for delete input types: it deletes at the internal
 * selection, trusting the "extension set the DOM selection just before
 * dispatching" handshake — which resolves against stale mappings mid-burst
 * and chews characters near the previous fix. Resolve the DOM selection (or
 * target range) robustly instead; drop the event when it cannot be located.
 */
export function handleUntrustedDelete(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent
): true | undefined {
  if (
    (event.inputType !== 'deleteContentBackward' &&
      event.inputType !== 'deleteContentForward') ||
    event.isTrusted
  ) {
    return undefined;
  }
  const at = resolveUntrustedEventTarget(editor, event, event.inputType);
  if (at === true) {
    return true;
  }
  try {
    applyFlushed(() => {
      editor.tf.select(at);
      if (RangeApi.isExpanded(at)) {
        // An expanded target IS the text to delete, whichever "direction" the
        // input type nominally carries — mirrors slate's own expanded-
        // selection handling for delete types.
        editor.tf.deleteFragment();
      } else if (event.inputType === 'deleteContentBackward') {
        editor.tf.deleteBackward();
      } else {
        editor.tf.deleteForward();
      }
    });
  } catch (e) {
    breadcrumbFailed(
      event.inputType,
      `insertReplacementTextPlugin: failed to apply ${event.inputType}`,
      e
    );
  }
  return true;
}

/**
 * Dispatch one beforeinput event to the robust handler for its input type.
 * Returns `true` when consumed (slate's default handling must not run),
 * `undefined` to let the default pipeline proceed.
 */
export function handleExtensionBeforeInput(
  editor: PlateEditor,
  event: ReplacementBeforeInputEvent
): true | undefined {
  switch (event.inputType) {
    case 'insertReplacementText':
      return handleInsertReplacementText(editor, event);
    case 'insertText':
      return handleUntrustedInsertText(editor, event);
    case 'deleteContentBackward':
    case 'deleteContentForward':
      return handleUntrustedDelete(editor, event);
    default:
      return undefined;
  }
}

/**
 * Shared plugin — every Plate editor surface must include it (note, email,
 * conversation, event-name template). Registered as a `handlers` plugin so it
 * runs before slate-react's own beforeinput processing; returning `true`
 * stops slate's default handling for the event. (The key keeps its original
 * name for stability even though the plugin now also hardens the extension-
 * dispatched `insertText` and delete events of multi-fix rewrites.)
 */
export const insertReplacementTextPlugin = createPlatePlugin({
  key: 'insertReplacementText',
  handlers: {
    onDOMBeforeInput: ({ editor, event }) => {
      // Plate wraps the native event in a React-like synthetic object at
      // runtime (convertDomEventToSyntheticEvent) but types it as Event. The
      // wrapper is built by spreading the native event, which loses prototype
      // members (getTargetRanges, dataTransfer) — unwrap to the native event.
      const native =
        (event as unknown as { nativeEvent?: Event }).nativeEvent ?? event;
      if (!(native instanceof InputEvent)) {
        return undefined;
      }
      return handleExtensionBeforeInput(editor, native);
    },
  },
});
